/**
 * Duplicate Ingredients Resolver Module
 * Handles loading pure HTML partial templates, detecting duplicate ingredient clusters, side-by-side diff resolution, and 1-click bulk auto-merging.
 */

import { getPluralUnit } from "./ingredientsController.js";
import { refuseCatalogWrite } from "./catalogGuard.js";
import { setNavBadge } from "./ui/shell.js";
import { BADGE, BUTTON, CARD, EMPTY_STATE } from "./ui/classes.js";
import { showDialog, hideDialog } from "./ui/dialog.js";
import { icon } from "./ui/icons.js";

let detectedDuplicateClusters = [];
let activeMergeCluster = null;
let selectedMergeFields = {};
let selectedSurvivingUid = "";

export async function loadDuplicatesTemplate() {
  const container = document.getElementById("sectionDuplicatesContainer");
  if (!container) return;

  if (document.getElementById("sectionDuplicates")) return;

  try {
    const resp = await fetch("templates/duplicates.html");
    if (!resp.ok) {
      throw new Error(`Failed to load duplicates template: ${resp.status} ${resp.statusText}`);
    }
    const html = await resp.text();
    container.innerHTML = html;
  } catch (err) {
    console.error("❌ Duplicates Template Load Error:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the Duplicates template: ${escapeHtml(err.message)}</div>`;
  }
}

export function getDetectedDuplicateClusters() {
  return detectedDuplicateClusters;
}

export function evaluateClusterConflicts(keyA, keyB, masterIngredients) {
  const itemA = masterIngredients[keyA] || {};
  const itemB = masterIngredients[keyB] || {};

  const fields = [
    { key: "display_name", getValue: (item) => (item.display_name || item.name || "").trim().toLowerCase() },
    { key: "canonical_unit", getValue: (item) => (item.canonical_unit || "gram").toLowerCase() },
    { key: "canonical_quantity", getValue: (item) => Number(item.canonical_quantity || 1) },
    { key: "pack_size", getValue: (item) => (typeof item.pack_size === "number" ? item.pack_size : null) },
    { key: "display_unit", getValue: (item) => (item.display_unit || "gram").toLowerCase() },
    { key: "subgroup_code", getValue: (item) => (item.subgroup_code || item.category || "vegetables").toLowerCase() },
    { key: "is_pantry_staple", getValue: (item) => item.is_pantry_staple === true },
    { key: "is_perishable", getValue: (item) => (typeof item.is_perishable === "boolean" ? item.is_perishable : null) },
    { key: "allergen_flags", getValue: (item) => JSON.stringify((item.allergen_flags || item.allergens || []).sort()) },
    { key: "dietary_preferences", getValue: (item) => JSON.stringify((item.dietary_preferences || []).sort()) },
    { key: "substitutes", getValue: (item) => JSON.stringify((item.substitutes || []).map(s => s.uid).sort()) }
  ];

  const conflicts = [];
  fields.forEach((field) => {
    const valA = field.getValue(itemA);
    const valB = field.getValue(itemB);
    if (valA !== valB) {
      conflicts.push(field.key);
    }
  });

  return conflicts;
}

export function normalizeIngredientKey(key, name) {
  const str = (name || key || "").toLowerCase().trim();
  return str
    .replace(/[^a-z0-9]/g, "")
    .replace(/(cloves|clove|pieces|piece|stalks|stalk|heads|head|bunches|bunch|leaves|leaf|slices|slice|breasts|breast|fillets|fillet|patty|patties|whole|fresh|dried|raw|cooked)$/g, "");
}

export function findDuplicateClusters(masterIngredients, customNormalizer) {
  const normalizer = customNormalizer || normalizeIngredientKey;
  const clusters = [];
  const ingKeys = Object.keys(masterIngredients || {});
  const visited = new Set();

  for (let i = 0; i < ingKeys.length; i++) {
    const keyA = ingKeys[i];
    if (visited.has(keyA)) continue;

    const itemA = masterIngredients[keyA] || {};
    const nameA = (itemA.display_name || itemA.name || itemA.name_singular || keyA).trim();
    const nameLowerA = nameA.toLowerCase();
    const stemA = normalizer(keyA, nameA);

    const clusterCandidates = [keyA];

    for (let j = i + 1; j < ingKeys.length; j++) {
      const keyB = ingKeys[j];
      if (visited.has(keyB)) continue;

      const itemB = masterIngredients[keyB] || {};
      const nameB = (itemB.display_name || itemB.name || itemB.name_singular || keyB).trim();
      const nameLowerB = nameB.toLowerCase();
      const stemB = normalizer(keyB, nameB);

      const isExactNameMatch = nameLowerA === nameLowerB;
      const isStemMatch = stemA.length >= 3 && stemA === stemB;

      if (isExactNameMatch || isStemMatch) {
        clusterCandidates.push(keyB);
        visited.add(keyB);
      }
    }

    if (clusterCandidates.length > 1) {
      visited.add(keyA);
      const conflicts = evaluateClusterConflicts(clusterCandidates[0], clusterCandidates[1], masterIngredients);
      clusters.push({
        title: nameA,
        stem: stemA,
        candidates: clusterCandidates,
        conflicts
      });
    }
  }

  detectedDuplicateClusters = clusters;

  setNavBadge("duplicates", clusters.length);

  renderDuplicatesSection(masterIngredients);
}

export function refreshDuplicatesEngine(masterIngredients, normalizeIngredientKey, showToast) {
  findDuplicateClusters(masterIngredients, normalizeIngredientKey);
  if (showToast) showToast("Refreshed duplicate detection engine!", "success");
}

export function renderDuplicatesSection(masterIngredients) {
  const container = document.getElementById("duplicatesContainer");
  const summaryBar = document.getElementById("duplicatesSummaryBar");
  if (!container) return;

  const cleanClusters = detectedDuplicateClusters.filter(c => c.conflicts.length === 0);

  if (detectedDuplicateClusters.length === 0) {
    if (summaryBar) summaryBar.style.display = "none";
    container.innerHTML = `
      <li class="${EMPTY_STATE.box}">
        ${icon("check-circle", EMPTY_STATE.icon)}
        <h3 class="${EMPTY_STATE.title}">No duplicates</h3>
        <p class="${EMPTY_STATE.text}">Every ingredient has its own name and ID stem.</p>
      </li>`;
    return;
  }

  const totalAffected = detectedDuplicateClusters.reduce((acc, c) => acc + c.candidates.length, 0);
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  if (summaryBar) {
    summaryBar.style.display = "flex";
    document.getElementById("dupClustersCountText").textContent = `${plural(detectedDuplicateClusters.length, "group", "groups")} of duplicates`;
    document.getElementById("dupItemsCountText").textContent = `${totalAffected} ingredients; ${plural(cleanClusters.length, "group has", "groups have")} no conflicting fields`;
  }

  container.innerHTML = detectedDuplicateClusters.map((cluster, idx) => {
    const badge = cluster.conflicts.length === 0
      ? `<span class="${BADGE.green}">No conflicts</span>`
      : `<span class="${BADGE.yellow}">${plural(cluster.conflicts.length, "conflicting field", "conflicting fields")}</span>`;

    const candidates = cluster.candidates.map((key) => {
      const item = (masterIngredients || {})[key] || {};
      return `
        <span class="inline-flex flex-col rounded-md bg-gray-50 px-2.5 py-1.5 inset-ring inset-ring-gray-200 dark:bg-white/5 dark:inset-ring-white/10">
          <span class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(item.display_name || item.name || key)}</span>
          <span class="font-mono text-xs text-gray-500 dark:text-gray-400">${escapeHtml(key)} · ${escapeHtml(item.display_unit || "gram")}</span>
        </span>`;
    }).join(`<span class="text-xs text-gray-400 dark:text-gray-500">vs</span>`);

    return `
      <li class="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between ${CARD.panel}">
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">${escapeHtml(cluster.title)}</h3>
            ${badge}
          </div>
          <div class="mt-2 flex flex-wrap items-center gap-2">${candidates}</div>
        </div>
        <button type="button" onclick="window.crmApp.openMergeDiffModal(${idx})" class="flex-none ${BUTTON.secondary}">Compare</button>
      </li>`;
  }).join("");
}

export async function autoMergeAllCleanClusters(db, masterIngredients, setDoc, doc, showToast, dismissToast, renderIngredientsTable, renderDashboardStats, normalizeIngredientKey) {
  if (refuseCatalogWrite(showToast)) return; // git owns the catalog (#1188)
  const cleanClusters = detectedDuplicateClusters.filter((c) => c.conflicts.length === 0);
  if (cleanClusters.length === 0) {
    if (showToast) showToast("No conflict-free clusters found to auto-merge.", "info");
    return;
  }

  const processingToast = showToast(`⏳ Auto-merging ${cleanClusters.length} conflict-free cluster(s) in Firestore...`, "processing");
  const snakeCaseRegex = /^[a-z0-9]+(_[a-z0-9]+)*$/;
  let mergedCount = 0;

  cleanClusters.forEach((cluster) => {
    const keyA = cluster.candidates[0];
    const keyB = cluster.candidates[1];

    const isSnakeA = snakeCaseRegex.test(keyA);
    const isSnakeB = snakeCaseRegex.test(keyB);

    let survivingUid = keyA;
    let obsoleteUid = keyB;

    if (isSnakeB && !isSnakeA) {
      survivingUid = keyB;
      obsoleteUid = keyA;
    }

    const itemA = masterIngredients[keyA] || {};
    const itemB = masterIngredients[keyB] || {};

    const displayUnit = itemA.display_unit || itemB.display_unit || "gram";
    const pluralUnit = itemA.plural || itemB.plural || getPluralUnit(displayUnit);
    const canonicalUnit = (itemA.canonical_unit || itemB.canonical_unit || "").toLowerCase() === "ml" ? "ml" : "gram";
    const canonicalQuantity = Number(itemA.canonical_quantity || itemB.canonical_quantity || 1);
    const subgroupCode = itemA.subgroup_code || itemB.subgroup_code || itemA.category || itemB.category || "pantry";
    const subgroupName = itemA.subgroup_name || itemB.subgroup_name || subgroupCode.replace(/_/g, " ");

    const mergedItem = {
      uid: survivingUid,
      display_name: itemA.display_name || itemA.name || itemB.display_name || itemB.name || survivingUid,
      display_unit: displayUnit,
      plural: pluralUnit,
      canonical_unit: canonicalUnit,
      canonical_quantity: canonicalQuantity,
      ...(typeof (itemA.pack_size ?? itemB.pack_size) === "number" ? { pack_size: itemA.pack_size ?? itemB.pack_size } : {}),
      subgroup_code: subgroupCode,
      subgroup_name: subgroupName,
      is_pantry_staple: (itemA.is_pantry_staple ?? itemB.is_pantry_staple) === true,
      ...(typeof (itemA.is_perishable ?? itemB.is_perishable) === "boolean" ? { is_perishable: itemA.is_perishable ?? itemB.is_perishable } : {}),
      allergen_flags: Array.isArray(itemA.allergen_flags) ? itemA.allergen_flags : (Array.isArray(itemB.allergen_flags) ? itemB.allergen_flags : []),
      dietary_preferences: Array.isArray(itemA.dietary_preferences) ? itemA.dietary_preferences : (Array.isArray(itemB.dietary_preferences) ? itemB.dietary_preferences : []),
      substitutes: Array.isArray(itemA.substitutes) ? itemA.substitutes : (Array.isArray(itemB.substitutes) ? itemB.substitutes : []),
      updated_at: new Date().toISOString()
    };

    delete mergedItem.id;
    Object.keys(mergedItem).forEach((k) => {
      if (mergedItem[k] === undefined) delete mergedItem[k];
    });

    masterIngredients[survivingUid] = mergedItem;
    delete masterIngredients[obsoleteUid];

    // Re-link substitute references
    Object.keys(masterIngredients).forEach((k) => {
      const ing = masterIngredients[k];
      if (ing.substitutes && Array.isArray(ing.substitutes)) {
        ing.substitutes.forEach((sub) => {
          if (sub.uid === obsoleteUid) sub.uid = survivingUid;
        });
      }
    });

    mergedCount++;
  });

  try {
    await setDoc(doc(db, "globals", "master_ingredients"), {
      ingredients: masterIngredients,
      count: Object.keys(masterIngredients).length,
      updated_at: new Date().toISOString()
    }, { merge: true });

    dismissToast(processingToast);
    findDuplicateClusters(masterIngredients, normalizeIngredientKey);
    if (renderIngredientsTable) renderIngredientsTable();
    if (renderDashboardStats) renderDashboardStats();

    showToast(`⚡ Successfully auto-merged ${mergedCount} conflict-free duplicate cluster(s)!`, "success");
  } catch (err) {
    dismissToast(processingToast);
    console.error("❌ Auto-Merge Error:", err);
    showToast("❌ Failed to execute auto-merge: " + err.message, "danger");
  }
}

export function openMergeDiffModal(clusterIndex, masterIngredients) {
  const cluster = detectedDuplicateClusters[clusterIndex];
  if (!cluster || cluster.candidates.length < 2) return;

  activeMergeCluster = cluster;
  const keyA = cluster.candidates[0];
  const keyB = cluster.candidates[1];

  const itemA = masterIngredients[keyA] || {};
  const itemB = masterIngredients[keyB] || {};

  const snakeCaseRegex = /^[a-z0-9]+(_[a-z0-9]+)*$/;
  const isSnakeA = snakeCaseRegex.test(keyA);
  const isSnakeB = snakeCaseRegex.test(keyB);

  if (isSnakeA && !isSnakeB) {
    selectedSurvivingUid = keyA;
  } else if (isSnakeB && !isSnakeA) {
    selectedSurvivingUid = keyB;
  } else {
    selectedSurvivingUid = keyA;
  }

  selectedMergeFields = {};

  document.getElementById("mergeHeaderCandidateA").innerHTML = candidateHeader(itemA, keyA);
  document.getElementById("mergeHeaderCandidateB").innerHTML = candidateHeader(itemB, keyB);

  // The ID recipe-builder should keep: the snake_case one when only one of them is.
  const survivingContainer = document.getElementById("mergeSurvivingUidContainer");
  if (survivingContainer) {
    const retired = selectedSurvivingUid === keyA ? keyB : keyA;
    const why = isSnakeA !== isSnakeB ? " (snake_case)" : "";
    survivingContainer.innerHTML = `<p class="text-sm text-gray-700 dark:text-gray-300">Suggested: keep <code class="font-mono text-xs font-semibold text-gray-900 dark:text-white">${escapeHtml(selectedSurvivingUid)}</code>${why} and retire <code class="font-mono text-xs">${escapeHtml(retired)}</code>.</p>`;
  }

  const fields = [
    { key: "display_name", label: "Display name" },
    { key: "display_unit", label: "Display unit", getValue: (item) => item.display_unit || "gram" },
    { key: "plural", label: "Plural unit", getValue: (item) => item.plural || getPluralUnit(item.display_unit || "gram") },
    { key: "canonical_unit", label: "Canonical unit", getValue: (item) => item.canonical_unit || "gram" },
    { key: "canonical_quantity", label: "Canonical quantity", getValue: (item) => String(item.canonical_quantity ?? 1) },
    { key: "pack_size", label: "Pack size", getValue: (item) => (typeof item.pack_size === "number" ? String(item.pack_size) : "Not set") },
    { key: "subgroup_code", label: "Aisle code", getValue: (item) => item.subgroup_code || item.category || "vegetables" },
    { key: "subgroup_name", label: "Aisle name", getValue: (item) => item.subgroup_name || (item.subgroup_code ? item.subgroup_code.replace(/_/g, " ") : "Vegetables") },
    { key: "is_pantry_staple", label: "Pantry staple", getValue: (item) => item.is_pantry_staple === true ? "Yes" : "No" },
    { key: "is_perishable", label: "Perishable", getValue: (item) => typeof item.is_perishable === "boolean" ? (item.is_perishable ? "Yes" : "No") : "Unknown" },
    { key: "allergen_flags", label: "Allergens", getValue: (item) => (item.allergen_flags || item.allergens || []).join(", ") || "None" },
    { key: "dietary_preferences", label: "Diets", getValue: (item) => (item.dietary_preferences || []).join(", ") || "None" },
    { key: "substitutes", label: "Substitutes", getValue: (item) => (item.substitutes || []).map(s => s.uid).join(", ") || "None" }
  ];

  const identicalLabels = [];
  const conflictingFields = [];

  fields.forEach((field) => {
    const valA = field.getValue ? field.getValue(itemA) : (itemA[field.key] ?? "—");
    const valB = field.getValue ? field.getValue(itemB) : (itemB[field.key] ?? "—");

    const strA = typeof valA === "object" ? JSON.stringify(valA) : String(valA);
    const strB = typeof valB === "object" ? JSON.stringify(valB) : String(valB);

    selectedMergeFields[field.key] = keyA;

    if (strA === strB) {
      identicalLabels.push(field.label);
    } else {
      conflictingFields.push({ field, strA, strB });
    }
  });

  const summaryEl = document.getElementById("mergeIdenticalSummaryContainer");
  if (summaryEl) {
    summaryEl.innerHTML = identicalLabels.length > 0
      ? `<p class="mt-2 text-sm text-gray-500 dark:text-gray-400"><span class="font-medium text-gray-700 dark:text-gray-300">Same in both:</span> ${escapeHtml(identicalLabels.join(", "))}.</p>`
      : "";
  }

  const tbody = document.getElementById("mergeDiffTableBody");
  if (tbody) {
    const value = (text) => (text ? escapeHtml(text) : `<span class="text-gray-400 italic dark:text-gray-500">empty</span>`);
    tbody.innerHTML = conflictingFields.length === 0
      ? `<tr><td colspan="3" class="px-4 py-6 text-center text-sm text-gray-500 sm:px-6 dark:text-gray-400">Every field is the same: only the IDs differ.</td></tr>`
      : conflictingFields.map(({ field, strA, strB }) => `
          <tr>
            <th scope="row" class="py-3 pr-3 pl-4 text-left text-sm font-medium text-gray-900 sm:pl-6 dark:text-white">${escapeHtml(field.label)}</th>
            <td class="px-3 py-3 text-sm text-gray-700 dark:text-gray-300">${value(strA)}</td>
            <td class="py-3 pr-4 pl-3 text-sm text-gray-700 sm:pr-6 dark:text-gray-300">${value(strB)}</td>
          </tr>`).join("");
  }

  showDialog("ingredientMergeModal");
}

function candidateHeader(item, key) {
  return `<span class="block">${escapeHtml(item.display_name || key)}</span><span class="block font-mono text-xs font-normal text-gray-500 dark:text-gray-400">${escapeHtml(key)}</span>`;
}

export function setSurvivingUid(uid) {
  selectedSurvivingUid = uid;
}

// Kept for window.crmApp: the dialog only compares now (#1210), so a choice is recorded and drawn nowhere.
export function selectMergeField(fieldKey, candidateKey) {
  selectedMergeFields[fieldKey] = candidateKey;
}

export async function executeIngredientMerge(db, masterIngredients, setDoc, doc, showToast, dismissToast, renderIngredientsTable, renderDashboardStats, normalizeIngredientKey) {
  if (refuseCatalogWrite(showToast)) return; // git owns the catalog (#1188)
  if (!activeMergeCluster || !selectedSurvivingUid) return;

  const keyA = activeMergeCluster.candidates[0];
  const keyB = activeMergeCluster.candidates[1];
  const obsoleteUid = selectedSurvivingUid === keyA ? keyB : keyA;

  hideDialog("ingredientMergeModal");

  const processingToast = showToast(`⏳ Merging '${obsoleteUid}' into '${selectedSurvivingUid}' in Firestore...`, "processing");

  const itemA = masterIngredients[keyA] || {};
  const itemB = masterIngredients[keyB] || {};

  const getSource = (fieldKey) => selectedMergeFields[fieldKey] === keyB ? itemB : itemA;

  const displayUnit = getSource("display_unit").display_unit || "gram";
  const pluralUnit = getSource("plural").plural || getPluralUnit(displayUnit);
  const canonicalUnit = (getSource("canonical_unit").canonical_unit || "").toLowerCase() === "ml" ? "ml" : "gram";
  const canonicalQuantity = Number(getSource("canonical_quantity").canonical_quantity || 1);
  const subgroupCode = getSource("subgroup_code").subgroup_code || getSource("subgroup_code").category || "vegetables";
  const subgroupName = getSource("subgroup_name").subgroup_name || subgroupCode.replace(/_/g, " ");

  const mergedItem = {
    uid: selectedSurvivingUid,
    display_name: getSource("display_name").display_name || getSource("display_name").name || selectedSurvivingUid,
    display_unit: displayUnit,
    plural: pluralUnit,
    canonical_unit: canonicalUnit,
    canonical_quantity: canonicalQuantity,
    ...(typeof getSource("pack_size").pack_size === "number" ? { pack_size: getSource("pack_size").pack_size } : {}),
    subgroup_code: subgroupCode,
    subgroup_name: subgroupName,
    is_pantry_staple: getSource("is_pantry_staple").is_pantry_staple === true,
    ...(typeof getSource("is_perishable").is_perishable === "boolean" ? { is_perishable: getSource("is_perishable").is_perishable } : {}),
    allergen_flags: getSource("allergen_flags").allergen_flags || getSource("allergen_flags").allergens || [],
    dietary_preferences: getSource("dietary_preferences").dietary_preferences || [],
    substitutes: getSource("substitutes").substitutes || [],
    updated_at: new Date().toISOString()
  };

  Object.keys(mergedItem).forEach((k) => {
    if (mergedItem[k] === undefined) delete mergedItem[k];
  });

  masterIngredients[selectedSurvivingUid] = mergedItem;
  delete masterIngredients[obsoleteUid];

  Object.keys(masterIngredients).forEach((key) => {
    const ing = masterIngredients[key];
    if (ing.substitutes && Array.isArray(ing.substitutes)) {
      ing.substitutes.forEach((sub) => {
        if (sub.uid === obsoleteUid) {
          sub.uid = selectedSurvivingUid;
        }
      });
    }
  });

  try {
    await setDoc(doc(db, "globals", "master_ingredients"), {
      ingredients: masterIngredients,
      count: Object.keys(masterIngredients).length,
      updated_at: new Date().toISOString()
    }, { merge: true });

    dismissToast(processingToast);
    findDuplicateClusters(masterIngredients, normalizeIngredientKey);
    if (renderIngredientsTable) renderIngredientsTable();
    if (renderDashboardStats) renderDashboardStats();

    showToast(`Merged '${obsoleteUid}' into '${selectedSurvivingUid}'. Note: Recipe & inverted index re-linking scheduled for batch migration.`, "success");
  } catch (err) {
    dismissToast(processingToast);
    console.error("❌ Merge Error:", err);
    showToast("❌ Failed to execute merge: " + err.message, "danger");
  }
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
